import type { CertificateResource } from "@/types/certificateTypes"
import { useTranslation } from "react-i18next"
import { FaShareAlt, FaDownload, FaEye } from "react-icons/fa"
import { Link } from "react-router-dom"
import { useRef } from "react"
import html2canvas from "html2canvas"
import { toast } from "react-toastify"
import { Button } from "react-bootstrap"

interface Props {
  certificate: CertificateResource
}

export default function CertificateCard({ certificate }: Props) {
  const { t } = useTranslation()
  const cardRef = useRef<HTMLDivElement>(null)

  const handleDownload = async () => {
    if (!cardRef.current) return

    const canvas = await html2canvas(cardRef.current, {
      useCORS: true,
      scale: 2,
      backgroundColor: "#ffffff",
    })

    const image = canvas.toDataURL("image/png")
    const link = document.createElement("a")
    link.download = `certificate-${certificate.user.name}.png`
    link.href = image
    link.click()
  }

  const handleShare = (
    platform: "facebook" | "twitter" | "linkedin" | "copy"
  ) => {
    const url = `${window.location.origin}/share/certificate/${certificate.uuid}`
    const message = t(
      "certificates.shareText",
      "I've earned a certificate in {{course}}!",
      { course: certificate.course.title }
    )

    if (platform === "copy") {
      navigator.clipboard.writeText(`${message} ${url}`)
      toast.success(
        t("certificates.copied", "Link and message copied to clipboard!")
      )
      return
    }

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(message)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    }

    window.open(shareUrls[platform], "_blank", "width=600,height=400")
  }

  // const sharePlatforms = [
  //   {
  //     key: "copy" as const,
  //     icon: <FaLink className="text-secondary" />,
  //     label: t("buttons.copyLink"),
  //   },
  //   {
  //     key: "facebook" as const,
  //     icon: <FaFacebookF className="text-primary" />,
  //     label: t("buttons.facebook"),
  //   },
  //   {
  //     key: "twitter" as const,
  //     icon: <FaTwitter className="text-info" />,
  //     label: t("buttons.twitter"),
  //   },
  //   {
  //     key: "linkedin" as const,
  //     icon: <FaLinkedinIn style={{ color: "#0077b5" }} />,
  //     label: t("buttons.linkedin"),
  //   },
  // ]

  return (
    <div className="card shadow-sm border-0 rounded-4 overflow-hidden mb-4">
      <div ref={cardRef}>
        <div className="position-relative">
          <img
            src={certificate.image_url}
            alt={certificate.course.title}
            className="card-img-top"
            style={{ height: "200px", objectFit: "cover" }}
            crossOrigin="anonymous"
          />
          <div className="position-absolute top-0 end-0 m-2">
            <span className="badge bg-primary rounded-pill px-3 py-2">
              {certificate.score}%
            </span>
          </div>
        </div>
        <div className="card-body p-4 pb-0">
          <h5 className="card-title fw-bold mb-1">
            {certificate.course.title}
          </h5>
          <p className="card-text text-muted small mb-3">
            {certificate.round.title}
          </p>
        </div>
      </div>

      <div className="card-body p-4 pt-2">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex gap-2">
            <Link
              to={`/share/certificate/${certificate.uuid}`}
              className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
              title={t("buttons.view", "View")}
            >
              <FaEye />
            </Link>
            <button
              onClick={handleDownload}
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
              title={t("buttons.download", "Download")}
            >
              <FaDownload />
            </button>
          </div>

          <Button
            variant="primary"
            onClick={() => handleShare("copy")}
            className="fs-6 py-1 pointer rounded-pill d-flex align-items-center gap-2 border-0 shadow-sm px-3"
          >
            <FaShareAlt />
            <span className="d-none d-sm-inline">{t("buttons.share")}</span>
          </Button>
          {/* <Dropdown align="end" className="z-3">

            <Dropdown.Menu className="border-0 shadow-lg rounded-3 z-3">
              {sharePlatforms.map(({ key, icon, label }) => (
                <Dropdown.Item
                  key={key}
                  onClick={() => handleShare(key)}
                  className="d-flex align-items-center gap-2 py-2"
                >
                  {icon} {label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown> */}
        </div>
      </div>
    </div>
  )
}
